import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Calculator, 
  FileText, 
  User, 
  MapPin, 
  Clock, 
  DollarSign, 
  Plus, 
  Minus, 
  Save,
  Send,
  Users,
  Wrench,
  Truck,
  Home,
  Plane,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const TRUCK_DAY_RATE = 155;
import { MaterialPicker } from './MaterialPicker';
import QuoteDocumentTab from './quote/QuoteDocumentTab';
import RentalLinePicker, { rentalBasisLabel } from './quote/RentalLinePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskBasedQuoteBuilder = ({ editingQuoteId, onSave, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reservedQuoteNumber, setReservedQuoteNumber] = useState(null);
  const reservationAttempted = useRef(false);  // Prevent double reservation
  const [tab, setTab] = useState('estimate');            // 'estimate' | 'document'
  const [quoteId, setQuoteId] = useState(editingQuoteId || null);  // survives the first save
  const [rentals, setRentals] = useState(null);  // Sunbelt rate sheet + rental margin
  useEffect(() => { axios.get(`${API}/rental-rates`).then(r => setRentals(r.data)).catch(() => setRentals(null)); }, []);
  
  const [quoteData, setQuoteData] = useState({
    // Basic quote info
    quote_number: '', // Reserved quote number
    client_name: '',
    client_email: '',
    client_phone: '',
    contact_person: '',
    project_title: '',
    description: '',
    project_location: '',
    estimator: user?.full_name || '',
    
    // Quote lines - this is the core of the system
    quote_lines: [
      {
        id: 1,
        type: 'labor', // labor, equipment, material, other
        task_description: '',
        // Labor specific
        num_workers: 1,
        hours_per_worker: 8,
        rate_type: 'regular', // regular, overtime, double_time
        hourly_rate: 65,
        // Equipment specific
        equipment_name: '',
        daily_rate: 0,
        days_needed: 1,
        // Material specific
        material_name: '',
        quantity: 1,
        unit: 'each',
        unit_cost: 0,
        // Other expenses
        expense_type: '',
        total_cost: 0,
        // Individual markup (optional)
        individual_markup_percentage: null
      }
    ],
    
    // Quote totals
    subtotal: 0,
    labor_subtotal: 0,
    non_labor_subtotal: 0,
    markup_percentage: 15,
    markup_amount: 0,
    tax_percentage: 8.25,
    tax_amount: 0,
    total_amount: 0,
    
    // Quote details
    quote_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    status: 'draft'
  });

  // Rate types with multipliers
  const rateTypes = [
    { value: 'regular', label: 'Regular Time', multiplier: 1.0 },
    { value: 'overtime', label: 'Overtime (1.5x)', multiplier: 1.5 },
    { value: 'double_time', label: 'Double Time (2x)', multiplier: 2.0 }
  ];

  // Line item types
  const lineTypes = [
    { value: 'labor', label: 'Labor/Task', icon: Users },
    { value: 'equipment', label: 'Equipment Rental', icon: Wrench },
    { value: 'truck', label: 'Service Truck', icon: Truck },
    { value: 'material', label: 'Materials', icon: Package },
    { value: 'other', label: 'Other Expenses', icon: DollarSign }
  ];

  // Other expense types
  const expenseTypes = [
    { value: 'lodging', label: 'Lodging' },
    { value: 'flights', label: 'Flights/Travel' },
    { value: 'meals', label: 'Meals & Per Diem' },
    { value: 'permits', label: 'Permits' },
    { value: 'crane', label: 'Crane Service' },
    { value: 'subcontractor', label: 'Subcontractor' },
    { value: 'misc', label: 'Miscellaneous' }
  ];

  // Reserve quote number on mount (for new quotes)
  useEffect(() => {
    const reserveQuoteNumber = async () => {
      if (!editingQuoteId && !reservationAttempted.current) {
        reservationAttempted.current = true;  // Mark as attempted
        
        try {
          const response = await axios.post(
            `${API}/quotes/reserve-number`,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          if (response.data.quote_number) {
            setReservedQuoteNumber(response.data.quote_number);
            setQuoteData(prev => ({
              ...prev,
              quote_number: response.data.quote_number
            }));
            toast.success(`Quote number ${response.data.quote_number} reserved`);
          }
        } catch (error) {
          console.error('Failed to reserve quote number:', error);
          toast.error('Failed to reserve quote number');
          reservationAttempted.current = false;  // Allow retry on error
        }
      }
    };
    
    reserveQuoteNumber();
  }, [editingQuoteId]);
  

  // Load existing quote data when editing
  useEffect(() => {
    const loadQuoteData = async () => {
      if (editingQuoteId) {
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/task-quotes/${editingQuoteId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.data) {
            const quote = response.data;
            console.log('Loading quote for editing:', quote);
            
            // Convert datetime to date format
            const formatDate = (dateStr) => {
              if (!dateStr) return '';
              try {
                return dateStr.split('T')[0]; // "2026-02-13T00:00:00" → "2026-02-13"
              } catch {
                return '';
              }
            };
            
            // Set quote data including quote_lines
            setQuoteData({
              quote_number: quote.quote_number || '',
              client_name: quote.client_name || '',
              client_email: quote.client_email || '',
              client_phone: quote.client_phone || '',
              contact_person: quote.contact_person || '',
              project_title: quote.project_title || '',
              description: quote.description || '',
              project_location: quote.project_location || '',
              estimator: quote.estimator || user?.full_name || '',
              quote_lines: quote.quote_lines || [],  // Include quote_lines!
              markup_percentage: quote.markup_percentage || 15,
              tax_percentage: quote.tax_percentage || 8.25,
              quote_date: formatDate(quote.quote_date),
              valid_until: formatDate(quote.valid_until)
            });
            
            toast.success(`Loaded quote ${quote.quote_number} for editing`);
          }
        } catch (error) {
          console.error('Failed to load quote:', error);
          toast.error('Failed to load quote data');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadQuoteData();
  }, [editingQuoteId]);

  // Handle cancel with reservation cleanup
  const handleCancel = async () => {
    if (reservedQuoteNumber && !editingQuoteId && !quoteId) {
      try {
        await axios.post(
          `${API}/quotes/cancel-reservation?quote_number=${reservedQuoteNumber}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        console.log(`Released reservation for ${reservedQuoteNumber}`);
      } catch (error) {
        console.error('Failed to cancel reservation:', error);
      }
    }
    // a quote was saved while the builder was open → let the list refresh
    if (quoteId && !editingQuoteId && onSave) { onSave(null); return; }
    if (onCancel) onCancel();
  };

  useEffect(() => {
    calculateTotals();
  }, [quoteData.quote_lines, quoteData.markup_percentage, quoteData.tax_percentage]);

  const calculateTotals = () => {
    let laborSubtotal = 0;
    let nonLaborSubtotal = 0;

    // Guard against undefined quote_lines
    const lines = quoteData.quote_lines || [];
    
    let markupAmount = 0;
    const lineMarkup = (line, lineTotal) => {
      const pct = line.individual_markup_percentage ?? quoteData.markup_percentage;
      return (lineTotal * (parseFloat(pct) || 0)) / 100;
    };

    lines.forEach((line, index) => {
      let lineTotal = 0;

      if (line.type === 'labor') {
        const rateMultiplier = rateTypes.find(r => r.value === line.rate_type)?.multiplier || 1;
        const effectiveRate = line.hourly_rate * rateMultiplier;
        lineTotal = line.num_workers * line.hours_per_worker * effectiveRate;
        laborSubtotal += lineTotal;
      } else if (line.type === 'equipment') {
        lineTotal = line.daily_rate * line.days_needed;
        nonLaborSubtotal += lineTotal;
        markupAmount += lineMarkup(line, lineTotal);
      } else if (line.type === 'truck') {
        lineTotal = (line.daily_rate || 0) * (line.days_needed || 0) * (line.num_trucks || 0);
        nonLaborSubtotal += lineTotal;
        markupAmount += lineMarkup(line, lineTotal);
      } else if (line.type === 'material') {
        lineTotal = line.quantity * line.unit_cost;
        nonLaborSubtotal += lineTotal;
        markupAmount += lineMarkup(line, lineTotal);
      } else if (line.type === 'other') {
        lineTotal = line.total_cost;
        nonLaborSubtotal += lineTotal;
        markupAmount += lineMarkup(line, lineTotal);
      }

      // Update the line total
      if (quoteData.quote_lines && quoteData.quote_lines[index]) {
        quoteData.quote_lines[index].calculated_total = lineTotal;
      }
    });

    const totalSubtotal = laborSubtotal + nonLaborSubtotal;
    
    // Markup only applies to non-labor items (per-line markup when set, otherwise the global %)
    const taxableAmount = totalSubtotal + markupAmount;
    const taxAmount = (taxableAmount * quoteData.tax_percentage) / 100;
    const totalAmount = taxableAmount + taxAmount;

    setQuoteData(prev => ({
      ...prev,
      subtotal: totalSubtotal,
      labor_subtotal: laborSubtotal,
      non_labor_subtotal: nonLaborSubtotal,
      markup_amount: markupAmount,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  const addQuoteLine = (type = 'labor') => {
    const newLine = {
      id: Date.now(),
      type,
      task_description: '',
      // Labor fields
      num_workers: 1,
      hours_per_worker: 8,
      rate_type: 'regular',
      hourly_rate: 65,
      // Equipment fields
      equipment_name: '',
      daily_rate: type === 'truck' ? TRUCK_DAY_RATE : 0,
      days_needed: 1,
      // Service truck fields
      num_trucks: 1,
      // Material fields
      material_name: '',
      quantity: 1,
      unit: 'each',
      unit_cost: 0,
      // Other expense fields
      expense_type: '',
      total_cost: 0,
      // Individual markup (optional) — our own truck charge isn't marked up unless you say so
      individual_markup_percentage: type === 'truck' ? 0 : null,
      calculated_total: 0
    };
    
    setQuoteData({
      ...quoteData,
      quote_lines: [...quoteData.quote_lines, newLine]
    });
  };

  const removeQuoteLine = (index) => {
    if (quoteData.quote_lines.length > 1) {
      const updatedLines = quoteData.quote_lines.filter((_, i) => i !== index);
      setQuoteData({
        ...quoteData,
        quote_lines: updatedLines
      });
    }
  };

  const updateQuoteLine = (index, field, value) => {
    const updatedLines = [...quoteData.quote_lines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };
    
    setQuoteData({
      ...quoteData,
      quote_lines: updatedLines
    });
  };

  const updateQuoteLineFields = (index, patch) => {
    const updatedLines = [...quoteData.quote_lines];
    updatedLines[index] = {
      ...updatedLines[index],
      ...patch
    };
    setQuoteData({
      ...quoteData,
      quote_lines: updatedLines
    });
  };

  const handleSave = async (status = 'draft', { stay = false } = {}) => {
    setSaving(true);
    
    try {
      const quotePayload = {
        ...quoteData,
        status
      };

      let response;
      if (quoteId) {
        response = await axios.put(`${API}/task-quotes/${quoteId}`, quotePayload);
      } else {
        response = await axios.post(`${API}/task-quotes`, quotePayload);
        if (response.data?.id) setQuoteId(response.data.id);
      }

      toast.success(stay ? 'Estimate saved' : `Quote ${status === 'sent' ? 'sent' : 'saved'} successfully!`);
      
      // Don't clear reserved number - it's now being used by the saved quote!
      // The reservation becomes the actual quote
      // setReservedQuoteNumber(null);  // REMOVED - keep it so cancel won't release
      
      if (onSave && !stay) {
        onSave(response.data);
      }
      return response.data;

    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error(`Failed to ${status === 'sent' ? 'send' : 'save'} quote`);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // The customer document needs a saved quote — save the estimate quietly first, then switch.
  const openDocumentTab = async () => {
    if (!quoteId) {
      if (!quoteData.client_name || !quoteData.project_title) {
        toast.error('Enter the client name and project title before building the quote document');
        return;
      }
      const saved = await handleSave('draft', { stay: true });
      if (!saved?.id) return;
    }
    setTab('document');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const renderLaborLine = (line, index) => (
    <div className="grid grid-cols-12 gap-2 items-end">
      <div className="col-span-3">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Task Description</Label>
        <Input
          value={line.task_description}
          onChange={(e) => updateQuoteLine(index, 'task_description', e.target.value)}
          placeholder="Install conveyor system..."
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block"># Guys</Label>
        <Input
          type="number"
          min="1"
          value={line.num_workers}
          onChange={(e) => updateQuoteLine(index, 'num_workers', parseInt(e.target.value) || 1)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Hours</Label>
        <Input
          type="number"
          step="0.5"
          value={line.hours_per_worker}
          onChange={(e) => updateQuoteLine(index, 'hours_per_worker', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Rate Type</Label>
        <select
          value={line.rate_type}
          onChange={(e) => updateQuoteLine(index, 'rate_type', e.target.value)}
          className="w-full px-2 py-2 bg-white border border-slate-300 rounded-md text-slate-900 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          {rateTypes.map(rate => (
            <option key={rate.value} value={rate.value}>{rate.label}</option>
          ))}
        </select>
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Base Rate</Label>
        <Input
          type="number"
          step="0.01"
          value={line.hourly_rate}
          onChange={(e) => updateQuoteLine(index, 'hourly_rate', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Line Total</Label>
        <div className="px-2 py-2 bg-slate-50 border border-slate-300 rounded-md text-green-700 text-sm font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
          {formatCurrency(line.calculated_total || 0)}
        </div>
      </div>
      
      <div className="col-span-1">
        {quoteData.quote_lines.length > 1 && (
          <Button
            onClick={() => removeQuoteLine(index)}
            size="sm"
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-900"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderTruckLine = (line, index) => (
    <div className="grid grid-cols-12 gap-2 items-end" data-testid={`truck-line-${index}`}>
      <div className="col-span-3">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Description</Label>
        <Input
          value={line.task_description}
          onChange={(e) => updateQuoteLine(index, 'task_description', e.target.value)}
          placeholder="Service truck"
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          data-testid={`truck-desc-${index}`}
        />
      </div>

      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block"># Trucks</Label>
        <Input
          type="number"
          min="0"
          step="1"
          value={line.num_trucks ?? 1}
          onChange={(e) => updateQuoteLine(index, 'num_trucks', parseInt(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          data-testid={`truck-count-${index}`}
        />
      </div>

      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Days</Label>
        <Input
          type="number"
          min="0"
          step="0.5"
          value={line.days_needed}
          onChange={(e) => updateQuoteLine(index, 'days_needed', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          data-testid={`truck-days-${index}`}
        />
      </div>

      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Rate / Day</Label>
        <Input
          type="number"
          step="0.01"
          value={line.daily_rate}
          onChange={(e) => updateQuoteLine(index, 'daily_rate', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          title={`Standard truck charge is $${TRUCK_DAY_RATE}/day`}
          data-testid={`truck-rate-${index}`}
        />
      </div>

      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Markup %</Label>
        <Input
          type="number"
          step="0.1"
          value={line.individual_markup_percentage ?? ''}
          onChange={(e) => updateQuoteLine(index, 'individual_markup_percentage', e.target.value !== '' ? parseFloat(e.target.value) : null)}
          placeholder={quoteData.markup_percentage.toString()}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          title="Truck lines default to 0% — it's our charge, not a pass-through cost. Blank = global markup."
          data-testid={`truck-markup-${index}`}
        />
      </div>

      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Line Total</Label>
        <div className="px-2 py-2 bg-slate-50 border border-slate-300 rounded-md text-green-700 text-sm font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis" data-testid={`truck-total-${index}`}>
          {formatCurrency(line.calculated_total || 0)}
        </div>
      </div>

      <div className="col-span-1">
        {quoteData.quote_lines.length > 1 && (
          <Button
            onClick={() => removeQuoteLine(index)}
            size="sm"
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 w-full px-2"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderEquipmentLine = (line, index) => {
    const isRental = !!line.rental_rate_id;
    const [rateLbl, qtyLbl] = rentalBasisLabel(line.rental_basis);
    const lockMargin = isRental && !rentals?.can_edit_margin;
    return (
    <div className="grid grid-cols-12 gap-2 items-end">
      <RentalLinePicker line={line} index={index} rentals={rentals} onPatch={(patch) => updateQuoteLineFields(index, patch)} />
      <div className="col-span-4">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Equipment Name</Label>
        <Input
          value={line.equipment_name}
          onChange={(e) => updateQuoteLine(index, 'equipment_name', e.target.value)}
          placeholder="20-ton crane, forklift, etc."
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          data-testid={`equipment-name-${index}`}
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">{isRental ? `${rateLbl} cost` : 'Daily Rate'}</Label>
        <Input
          type="number"
          step="0.01"
          value={line.daily_rate}
          readOnly={isRental}
          onChange={(e) => updateQuoteLine(index, 'daily_rate', parseFloat(e.target.value) || 0)}
          className={`border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500 ${isRental ? 'bg-orange-50' : 'bg-white'}`}
          title={isRental ? `What ${line.vendor || 'Sunbelt'} charges us — from the rate sheet` : undefined}
          data-testid={`equipment-rate-${index}`}
        />
      </div>
      
      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">{isRental ? qtyLbl : 'Days'}</Label>
        <Input
          type="number"
          step="0.5"
          value={line.days_needed}
          onChange={(e) => updateQuoteLine(index, 'days_needed', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          data-testid={`equipment-qty-${index}`}
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">{isRental ? 'Margin %' : 'Markup %'}</Label>
        <Input
          type="number"
          step="0.1"
          value={line.individual_markup_percentage ?? ''}
          readOnly={lockMargin}
          onChange={(e) => updateQuoteLine(index, 'individual_markup_percentage', e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={quoteData.markup_percentage.toString()}
          className={`border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500 ${lockMargin ? 'bg-slate-100 text-slate-500' : 'bg-white'}`}
          title={isRental ? (lockMargin ? 'Rental margin — only a super admin can change it' : 'Rental margin on this line (super admin override)') : `Leave blank to use global markup (${quoteData.markup_percentage}%)`}
          data-testid={`equipment-markup-${index}`}
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Line Total</Label>
        <div className="px-2 py-2 bg-slate-50 border border-slate-300 rounded-md text-green-700 text-sm font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis" data-testid={`equipment-total-${index}`}>
          {formatCurrency(line.calculated_total || 0)}
        </div>
      </div>
      
      <div className="col-span-1">
        {quoteData.quote_lines.length > 1 && (
          <Button
            onClick={() => removeQuoteLine(index)}
            size="sm"
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-900"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
    );
  };

  const renderMaterialLine = (line, index) => (
    <div className="grid grid-cols-12 gap-2 items-end">
      <div className="col-span-3">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Material Name</Label>
        <MaterialPicker
          value={line.material_name}
          onApply={(patch) => updateQuoteLineFields(index, patch)}
        />
      </div>
      
      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Quantity</Label>
        <Input
          type="number"
          step="0.1"
          value={line.quantity}
          onChange={(e) => updateQuoteLine(index, 'quantity', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-1">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Unit</Label>
        <select
          value={line.unit}
          onChange={(e) => updateQuoteLine(index, 'unit', e.target.value)}
          className="w-full px-2 py-2 bg-white border border-slate-300 rounded-md text-slate-900 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="each">Each</option>
          <option value="lbs">Lbs</option>
          <option value="ft">Ft</option>
          <option value="sq_ft">Sq Ft</option>
          <option value="tons">Tons</option>
        </select>
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Unit Cost</Label>
        <Input
          type="number"
          step="0.01"
          value={line.unit_cost}
          onChange={(e) => updateQuoteLine(index, 'unit_cost', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Markup %</Label>
        <Input
          type="number"
          step="0.1"
          value={line.individual_markup_percentage ?? ''}
          onChange={(e) => updateQuoteLine(index, 'individual_markup_percentage', e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={quoteData.markup_percentage.toString()}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          title={`Leave blank to use global markup (${quoteData.markup_percentage}%)`}
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Line Total</Label>
        <div className="px-2 py-2 bg-slate-50 border border-slate-300 rounded-md text-green-700 text-sm font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
          {formatCurrency(line.calculated_total || 0)}
        </div>
      </div>
      
      <div className="col-span-1">
        {quoteData.quote_lines.length > 1 && (
          <Button
            onClick={() => removeQuoteLine(index)}
            size="sm"
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-900"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderOtherLine = (line, index) => (
    <div className="grid grid-cols-12 gap-2 items-end">
      <div className="col-span-3">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Expense Type</Label>
        <select
          value={line.expense_type}
          onChange={(e) => updateQuoteLine(index, 'expense_type', e.target.value)}
          className="w-full px-2 py-2 bg-white border border-slate-300 rounded-md text-slate-900 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="">Select type</option>
          {expenseTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Description</Label>
        <Input
          value={line.task_description}
          onChange={(e) => updateQuoteLine(index, 'task_description', e.target.value)}
          placeholder="Hotel for 3 nights, flight to site, etc."
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Total Cost</Label>
        <Input
          type="number"
          step="0.01"
          value={line.total_cost}
          onChange={(e) => updateQuoteLine(index, 'total_cost', parseFloat(e.target.value) || 0)}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Markup %</Label>
        <Input
          type="number"
          step="0.1"
          value={line.individual_markup_percentage ?? ''}
          onChange={(e) => updateQuoteLine(index, 'individual_markup_percentage', e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={quoteData.markup_percentage.toString()}
          className="bg-white border-slate-300 text-slate-900 text-sm px-2 focus:border-blue-500 focus:ring-blue-500"
          title={`Leave blank to use global markup (${quoteData.markup_percentage}%)`}
        />
      </div>
      
      <div className="col-span-2">
        <Label className="text-slate-700 font-medium text-sm mb-1 block">Line Total</Label>
        <div className="px-2 py-2 bg-slate-50 border border-slate-300 rounded-md text-green-700 text-sm font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
          {formatCurrency(line.total_cost || 0)}
        </div>
      </div>
      
      <div className="col-span-1">
        {quoteData.quote_lines.length > 1 && (
          <Button
            onClick={() => removeQuoteLine(index)}
            size="sm"
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-900"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {editingQuoteId ? 'Edit Quote' : 'New Task-Based Quote'}
              {quoteData.quote_number && (
                <span className="ml-4 text-xl text-blue-700">
                  #{quoteData.quote_number}
                </span>
              )}
            </h1>
            <p className="text-slate-500">Build quotes task by task with workers, rates, and expenses</p>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              {quoteId ? 'Close' : 'Cancel'}
            </Button>
            {tab === 'estimate' && (
              <Button
                onClick={() => handleSave('draft')}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Quote'}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-1 border-b border-slate-200" data-testid="quote-builder-tabs">
          <button onClick={() => setTab('estimate')} className={`px-4 py-2 text-sm rounded-t border-b-2 -mb-px ${tab === 'estimate' ? 'border-blue-500 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-900'}`} data-testid="quote-tab-estimate">Estimate</button>
          <button onClick={openDocumentTab} className={`px-4 py-2 text-sm rounded-t border-b-2 -mb-px ${tab === 'document' ? 'border-blue-500 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-900'}`} data-testid="quote-tab-document">Quote Document</button>
        </div>
      </div>

      {tab === 'document' && quoteId && <QuoteDocumentTab quoteId={quoteId} user={user} />}

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${tab === 'document' ? 'hidden' : ''}`}>
        {/* Main Quote Building Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
              <CardTitle className="text-slate-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Quote Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">Client Name *</Label>
                  <Input
                    value={quoteData.client_name}
                    onChange={(e) => setQuoteData({...quoteData, client_name: e.target.value})}
                    placeholder="Company Name"
                    className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">Contact Person</Label>
                  <Input
                    value={quoteData.contact_person}
                    onChange={(e) => setQuoteData({...quoteData, contact_person: e.target.value})}
                    placeholder="John Smith"
                    className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">Project Title *</Label>
                  <Input
                    value={quoteData.project_title}
                    onChange={(e) => setQuoteData({...quoteData, project_title: e.target.value})}
                    placeholder="Conveyor Installation"
                    className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">Project Location</Label>
                  <Input
                    value={quoteData.project_location}
                    onChange={(e) => setQuoteData({...quoteData, project_location: e.target.value})}
                    placeholder="Detroit, MI"
                    className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-slate-700 font-medium mb-2 block">Project Description</Label>
                <Textarea
                  value={quoteData.description}
                  onChange={(e) => setQuoteData({...quoteData, description: e.target.value})}
                  placeholder="Brief description of the work to be performed..."
                  className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Quote Lines */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <CardTitle className="text-slate-900 flex items-center whitespace-nowrap">
                  <Calculator className="h-5 w-5 mr-2 text-blue-500" />
                  Quote Lines
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2" data-testid="quote-line-add-buttons">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">Add</span>
                  {lineTypes.map(type => (
                    <Button
                      key={type.value}
                      onClick={() => addQuoteLine(type.value)}
                      size="sm"
                      variant="outline"
                      data-testid={`add-line-${type.value}`}
                      className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white bg-white"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      <type.icon className="h-4 w-4 mr-1" />
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {(quoteData.quote_lines || []).map((line, index) => (
                  <div key={line.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center mb-3">
                      <span className="text-slate-900 font-medium mr-2">
                        Line #{index + 1}
                      </span>
                      <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded capitalize">
                        {line.type}
                      </span>
                    </div>
                    
                    {line.type === 'labor' && renderLaborLine(line, index)}
                    {line.type === 'equipment' && renderEquipmentLine(line, index)}
                    {line.type === 'truck' && renderTruckLine(line, index)}
                    {line.type === 'material' && renderMaterialLine(line, index)}
                    {line.type === 'other' && renderOtherLine(line, index)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quote Totals Sidebar */}
        <div className="space-y-6">
          <Card className="bg-white border-slate-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
              <CardTitle className="text-slate-900 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Quote Totals
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium mb-2 block">Labor Subtotal:</span>
                  <span className="text-slate-900 font-semibold">{formatCurrency(quoteData.labor_subtotal)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium mb-2 block">Materials/Equipment/Other:</span>
                  <span className="text-slate-900 font-semibold">{formatCurrency(quoteData.non_labor_subtotal)}</span>
                </div>
                
                <hr className="border-slate-300" />
                
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium mb-2 block">Subtotal:</span>
                  <span className="text-slate-900 font-semibold">{formatCurrency(quoteData.subtotal)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-slate-700 font-medium mb-2 block">Markup (non-labor only):</span>
                    <span className="text-xs text-slate-500">Applied to materials, equipment & other expenses</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={quoteData.markup_percentage}
                      onChange={(e) => setQuoteData({...quoteData, markup_percentage: parseFloat(e.target.value) || 0})}
                      className="w-16 h-8 bg-white border-slate-300 text-slate-900 text-sm"
                    />
                    <span className="text-slate-700 font-medium text-sm mb-1 block">%</span>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium mb-2 block">Markup Amount:</span>
                  <span className="text-slate-900 font-semibold">{formatCurrency(quoteData.markup_amount)}</span>
                </div>
                
                <hr className="border-slate-300" />
                
                <div className="flex justify-between items-center">
                  <span className="text-slate-700 font-medium mb-2 block">Tax:</span>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={quoteData.tax_percentage}
                      onChange={(e) => setQuoteData({...quoteData, tax_percentage: parseFloat(e.target.value) || 0})}
                      className="w-16 h-8 bg-white border-slate-300 text-slate-900 text-sm"
                    />
                    <span className="text-slate-700 font-medium text-sm mb-1 block">%</span>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium mb-2 block">Tax Amount:</span>
                  <span className="text-slate-900 font-semibold">{formatCurrency(quoteData.tax_amount)}</span>
                </div>
                
                <hr className="border-slate-300" />
                
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-slate-900">Total:</span>
                  <span className="text-green-700 font-mono">{formatCurrency(quoteData.total_amount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quote Settings */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
              <CardTitle className="text-slate-900 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-blue-500" />
                Quote Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="card-body space-y-4">
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">Quote Date</Label>
                <Input
                  type="date"
                  value={quoteData.quote_date}
                  onChange={(e) => setQuoteData({...quoteData, quote_date: e.target.value})}
                  className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">Valid Until</Label>
                <Input
                  type="date"
                  value={quoteData.valid_until}
                  onChange={(e) => setQuoteData({...quoteData, valid_until: e.target.value})}
                  className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">Estimator</Label>
                <Input
                  value={quoteData.estimator}
                  onChange={(e) => setQuoteData({...quoteData, estimator: e.target.value})}
                  className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TaskBasedQuoteBuilder;